import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";

import RegretSwipeAlert from "shared/components/RegretSwipeAlert";
import PageLoading from "shared/components/PageLoading";
import useSessionBlobUploader from "shared/hooks/useSessionBlobUploader";
import { useHasMetPhotoRequirements } from "shared/hooks/useHasMetPhotoRequirements";

import AuthedGlobalElements from "routers/Desktop/AuthedGlobalElements";
import useScrollToTopOnNavigation from "routers/shared/hooks/useScrollToTopOnNavigation";
import usePayPalErrorHandler from "routers/shared/hooks/usePayPalErrorHandler";
import useInstantEvents from "routers/shared/hooks/useInstantEvents";
import useRushHourModal from "routers/shared/hooks/useRushHourModal";
import useSiteRedirect from "routers/shared/hooks/useSiteRedirect";
import { useMarkSmsForcedMigration } from "analytics/sms";
import { ROUTES, UNAUTHED_ROUTE_LIST, LEGACY_ROUTES } from "routers/shared/util/consts";

const Stacks = React.lazy(() => import(/* webpackChunkName: "spa_stacks" */ "apps/quickmatch"));
const Discovery = React.lazy(() => import(/* webpackChunkName: "spa_discovery" */ "apps/discovery"));
const Profile = React.lazy(() => import(/* webpackChunkName: "spa_profile" */ "apps/profile"));
const Likes = React.lazy(() => import(/* webpackChunkName: "spa_likes" */ "apps/userrows"));
const Messages = React.lazy(() => import(/* webpackChunkName: "spa_messages" */ "apps/messages"));
const StandaloneConversation = React.lazy(() => import(/* webpackChunkName: "spa_standalone_conversation" */ "apps/messenger/containers/Conversation"));
const Settings = React.lazy(() => import(/* webpackChunkName: "spa_settings" */ "settings"));
const LostPassword = React.lazy(() => import(/* webpackChunkName: "spa_lost_password" */ "apps/lost_password"));
const RealName = React.lazy(() => import(/* webpackChunkName: "spa_realname" */ "apps/realnames"));
const Upgrade = React.lazy(() => import(/* webpackChunkName: "spa_upgrade" */ "apps/upgrade"));
const Incognito = React.lazy(() => import(/* webpackChunkName: "spa_incognito" */ "apps/incognito_packages"));
const Onboarding = React.lazy(() => import(/* webpackChunkName: "spa_onboarding" */ "apps/onboarding"));
const Offboarding = React.lazy(() => import(/* webpackChunkName: "spa_offboarding" */ "apps/offboarding/App"));
const PasswordReboarding = React.lazy(() => import(/* webpackChunkName: "spa_password_reboarding" */ "apps/password_reboarding"));
const PhoneWall = React.lazy(() => import(/* webpackChunkName: "spa_phone_wall" */ "apps/phonewall"));
const LogoutTrigger = React.lazy(() => import(/* webpackChunkName: "spa_logout_trigger" */ "apps/logout_trigger"));
const NotFound = React.lazy(() => import(/* webpackChunkName: "spa_404" */ "./NotFound"));
const Legal = React.lazy(() => import(/* webpackChunkName: "spa_legal" */ "apps/legal"));
const LegalPurchaseTerms = React.lazy(() => import(/* webpackChunkName: "spa_legal_purchase_terms" */ "apps/legal_purchase_terms"));
const About = React.lazy(() => import(/* webpackChunkName: "spa_about" */ "apps/about"));
const Press = React.lazy(() => import(/* webpackChunkName: "spa_press" */ "apps/press"));
const Careers = React.lazy(() => import(/* webpackChunkName: "spa_careers" */ "apps/careers"));
const GDPR = React.lazy(() => import(/* webpackChunkName: "spa_gdpr" */ "apps/gdpr"));

const AuthedRoutes = ({ userid, location }) => {
	// Show Rush Hour modal, if needed.
	useRushHourModal(location, userid);

	// Handle any non-SPA redirects.
	useSiteRedirect(location);

	// Show PayPal errors, if needed.
	usePayPalErrorHandler(location);

	// Initialize InstantEvents.
	useInstantEvents();

	// Scroll to the top on each page nav.
	useScrollToTopOnNavigation(location);

	// Upload info about the current session.
	useSessionBlobUploader(userid);

	useMarkSmsForcedMigration(userid);

	// Check & mark whether user is photo-compliant
	useHasMetPhotoRequirements(userid, location);

	return (
		<>
			<Suspense fallback={<PageLoading />}>
				<RegretSwipeAlert />
				<Switch>
					<Route
						exact
						path={ROUTES.Doubletake}
						component={Stacks}
					/>
					<Route
						exact
						path={ROUTES.Discovery}
						component={Discovery}
					/>
					<Route
						exact
						path={[
							...ROUTES.ProfileMain,
							...ROUTES.ProfileQuestions,
							...ROUTES.ProfilePhotos,
						]}
						component={Profile}
					/>
					<Route
						exact
						path={[
							...ROUTES.WhoLikesYou,
							...ROUTES.WhoYouLike,
							...ROUTES.Intros,
						]}
						component={Likes}
					/>
					<Route
						exact
						path={ROUTES.Messages}
						component={Messages}
					/>
					<Route
						exact
						path={ROUTES.StandaloneConversation}
						component={StandaloneConversation}
					/>
					<Route
						exact
						path={ROUTES.Settings}
						component={Settings}
					/>
					<Route
						exact
						path={ROUTES.LostPassword}
						component={LostPassword}
					/>
					<Route
						exact
						path={ROUTES.RealName}
						component={RealName}
					/>
					<Route
						exact
						path={ROUTES.Upgrade}
						component={Upgrade}
					/>
					<Route
						exact
						path={ROUTES.Incognito}
						component={Incognito}
					/>
					<Route
						exact
						path={ROUTES.Onboarding}
						component={Onboarding}
					/>
					<Route
						exact
						path={ROUTES.Offboarding}
						component={Offboarding}
					/>
					<Route
						exact
						path={ROUTES.PhoneWall}
						component={PhoneWall}
					/>
					<Route
						exact
						path={ROUTES.PasswordReboarding}
						component={PasswordReboarding}
					/>
					<Route
						exact
						path={ROUTES.Logout}
						component={LogoutTrigger}
					/>
					<Route
						exact
						path={ROUTES.Legal}
						component={Legal}
					/>
					<Route
						exact
						path={ROUTES.LegalPurchaseTerms}
						component={LegalPurchaseTerms}
					/>
					<Route
						exact
						path={ROUTES.About}
						component={About}
					/>
					<Route
						exact
						path={ROUTES.Press}
						component={Press}
					/>
					<Route
						exact
						path={ROUTES.Careers}
						component={Careers}
					/>
					<Route
						exact
						path={ROUTES.GDPR}
						component={GDPR}
					/>
					{LEGACY_ROUTES.map((legacyRoute) =>
						<Redirect exact from={legacyRoute} to={ROUTES.Doubletake[0]} key={legacyRoute} />,
					)}
					<Redirect
						exact
						from="/stacks"
						to={{
							pathname: ROUTES.Doubletake[0],
							search: location.search,
						}}
					/>
					<Redirect
						exact
						from="/questions"
						to={{
							pathname: `/profile/${userid}/questions`,
							search: location.search,
						}}
					/>

					{/* Redirect all unauthed routes to Doubletake */}
					{UNAUTHED_ROUTE_LIST.map((unauthedRoute) => (
						<Redirect
							key={unauthedRoute}
							exact
							from={unauthedRoute}
							to={ROUTES.Doubletake[0]}
						/>
					))}

					<Route component={NotFound} />
				</Switch>
			</Suspense>
			<AuthedGlobalElements userid={userid} />
		</>
	);
};

AuthedRoutes.propTypes = {
	userid: PropTypes.string.isRequired,
	location: PropTypes.object.isRequired,
};

export default AuthedRoutes;



// WEBPACK FOOTER //
// ./src/routers/Desktop/AuthedRoutes.jsx